// components/views/profile/QboPreflightCheck.jsx
import React, { Component } from 'react';
import QboAPI from '../../../services/qboService';

const CHECK_LABELS = {
  subscription: 'Subscription tier',
  track_locations: 'Track Locations enabled',
  sales_tax: 'Sales tax (AST)',
  currency: 'Currency',
  accounts_receivable: 'Accounts Receivable',
  bank_account: 'Bank account for payouts'
};

class QboPreflightCheck extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      needsReconnect: false,
      ready: false,
      summary: '',
      checks: [],
      selectedBankId: '',
      rechecking: false
    };
  }

  componentDidMount() {
    this.runPreflight();
  }

  runPreflight = async (isRecheck = false) => {
    this.setState(isRecheck ? { rechecking: true, error: null } : { loading: true, error: null });

    try {
      const result = await QboAPI.getPreflight(this.props.installerId);

      const checks = Array.isArray(result.checks) ? result.checks : [];
      const bankCheck = checks.find(c => c.key === 'bank_account');
      const banks = bankCheck && Array.isArray(bankCheck.value) ? bankCheck.value : [];

      // Preserve prior selection if still valid, else pick the first bank
      const priorSelection = this.state.selectedBankId;
      const stillValid = banks.some(b => String(b.id) === String(priorSelection));
      const selectedBankId = stillValid
        ? priorSelection
        : (banks.length > 0 ? String(banks[0].id) : '');

      this.setState({
        loading: false,
        rechecking: false,
        needsReconnect: false,
        ready: !!result.ready,
        summary: result.summary || '',
        checks,
        selectedBankId
      });
    } catch (error) {
      console.error('[QboPreflightCheck] preflight error:', error);
      const status = error.response?.status;
      const message = error.response?.data?.message;

      if (status === 400 && message && /No QBO connection/i.test(message)) {
        this.setState({
          loading: false,
          rechecking: false,
          needsReconnect: true,
          error: message
        });
      } else {
        this.setState({
          loading: false,
          rechecking: false,
          needsReconnect: false,
          error: message || 'Failed to run QuickBooks preflight check. Please try again.'
        });
      }
    }
  };

  handleBankSelect = (e) => {
    this.setState({ selectedBankId: e.target.value });
  };

  renderCheckIcon = (check) => {
    if (check.pass) {
      return <i className="fa-sharp fa-solid fa-circle-check text-success" />;
    }
    return <i className="fa-sharp fa-solid fa-triangle-exclamation text-warning" />;
  };

  renderBankSelector = (check) => {
    const banks = Array.isArray(check.value) ? check.value : [];
    if (!check.pass || banks.length === 0) return null;

    return (
      <div className="mt-2">
        <label className="small text-muted mb-1">Select the bank account that should receive Stripe payouts:</label>
        <select
          className="form-control form-control-sm"
          value={this.state.selectedBankId}
          onChange={this.handleBankSelect}
        >
          {banks.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>
    );
  };

  render() {
    const { loading, error, needsReconnect, ready, summary, checks, selectedBankId, rechecking } = this.state;

    if (loading) {
      return (
        <div className="qbo-preflight text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
            <span className="sr-only">Checking...</span>
          </div>
          <span className="text-muted">Checking your QuickBooks settings...</span>
        </div>
      );
    }

    if (needsReconnect) {
      return (
        <div className="alert alert-warning mb-0">
          <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2" />
          <strong>No active QuickBooks connection.</strong>
          <div className="small mt-1">{error} Use the Connect QuickBooks button above to reauthorize.</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="alert alert-danger mb-0">
          <i className="fa-sharp fa-regular fa-exclamation-circle mr-2" />
          {error}
          <button
            className="btn btn-sm btn-outline-danger ml-3"
            onClick={() => this.runPreflight(true)}
            disabled={rechecking}
          >
            {rechecking ? 'Checking...' : 'Try Again'}
          </button>
        </div>
      );
    }

    const bankCheckSelected = !!selectedBankId || !checks.some(c => c.key === 'bank_account' && c.pass);
    const canContinue = ready && bankCheckSelected;

    return (
      <div className="qbo-preflight">
        <div className={`alert ${ready ? 'alert-success' : 'alert-warning'} py-2 mb-3`}>
          <div className="d-flex align-items-center">
            <i className={`fa-sharp fa-solid ${ready ? 'fa-circle-check' : 'fa-triangle-exclamation'} mr-2`} />
            <strong>{summary || (ready ? 'All preflight checks passed.' : 'Preflight checks need attention.')}</strong>
          </div>
        </div>

        <ul className="list-group mb-3">
          {checks.map(check => (
            <li key={check.key} className="list-group-item">
              <div className="d-flex align-items-start">
                <div className="mr-3 mt-1" style={{ fontSize: '1.1rem' }}>
                  {this.renderCheckIcon(check)}
                </div>
                <div className="flex-grow-1">
                  <div><strong>{CHECK_LABELS[check.key] || check.key}</strong></div>
                  <div className="small text-muted">{check.message}</div>
                  {check.key === 'bank_account' && this.renderBankSelector(check)}
                </div>
              </div>
            </li>
          ))}
        </ul>

        <div className="d-flex justify-content-end">
          <button
            className="btn btn-outline-secondary mr-2"
            onClick={() => this.runPreflight(true)}
            disabled={rechecking}
          >
            {rechecking ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" />
                Checking...
              </>
            ) : (
              <>
                <i className="fa-sharp fa-regular fa-rotate mr-1" />
                Re-check
              </>
            )}
          </button>
          <button
            className="btn btn-success"
            onClick={() => this.props.onContinue && this.props.onContinue({ default: selectedBankId })}
            disabled={!canContinue}
            title={!ready ? 'Fix the failing checks above first' : ''}
          >
            <i className="fa-sharp fa-regular fa-arrow-right mr-1" />
            Run QBO Setup
          </button>
        </div>
      </div>
    );
  }
}

export default QboPreflightCheck;
